import { useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  LinearProgress,
  Alert,
  CircularProgress,
  Collapse,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Storage as StorageIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  InsertDriveFile as FileIcon,
  Tune as TuneIcon,
  Check as CheckIcon,
  DonutLarge as DonutLargeIcon,
  DonutSmall as DonutSmallIcon,
  Lock as LockIcon,
  NavigateNext as NavigateNextIcon,
  WifiOff as WifiOffIcon,
  SystemUpdateAlt as SystemUpdateIcon,
  WarningAmberRounded as WarningAmberIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { deviceService } from '@/lib/device.service';
import { formatBytes } from '@/lib/formatters';
import { colors } from '@/theme/tokens';
import type { FilesystemEntry } from '@/types';
import { FirmwareOutdatedError } from '@/types';

// ─── Types ───────────────────────────────────────────────────────────

interface StorageExplorerProps {
  deviceId: string;
  isOnline: boolean;
  // Power-user controls (chart toggle + size-unit picker) live in the
  // card header. They're hidden by default because non-technical
  // viewers don't need a sunburst or to switch between B/KB/MB/GB —
  // the auto-formatted size column already covers most needs. Set
  // true on call sites that want the full UI (e.g. an admin-only
  // diagnostics surface).
  showAdvancedControls?: boolean;
}

interface TreeNode {
  name: string;
  path: string;
  size: string;
  sizeBytes: number;
  percent: number;       // % of parent
  children: TreeNode[];
  loaded: boolean;
  loading: boolean;
  expanded: boolean;
  isLeaf: boolean;       // true when the entry is a regular file
  // True while the row is awaiting its recursive size from /filesystem/size.
  // The Size column renders a tiny spinner instead of "—" so a still-
  // measuring row isn't mistaken for one that failed.
  sizingPending?: boolean;
  // True when the size measurement returned -1 (timeout / device error).
  // The Size column renders "—" and the row gets a lock icon. Distinct
  // from sizingPending so retry vs. failed states stay readable.
  permissionDenied?: boolean;
}

// ─── Formatting helpers ──────────────────────────────────────────────

// Size unit override. 'auto' (default) picks the best fit per-value, the
// rest force every row to the same unit so the user can eyeball totals
// without mental arithmetic. 'b' prints the raw byte count with locale
// grouping ("1,234,567 B"). Hidden behind the gear icon in the card
// header; persisted so the pick stays across sessions.
type SizeUnit = 'auto' | 'b' | 'kb' | 'mb' | 'gb' | 'tb';
const SIZE_UNIT_MULTIPLIER: Record<Exclude<SizeUnit, 'auto' | 'b'>, number> = {
  kb: 1024,
  mb: 1024 ** 2,
  gb: 1024 ** 3,
  tb: 1024 ** 4,
};
function formatSize(bytes: number, unit: SizeUnit): string {
  if (unit === 'auto') return formatBytes(bytes);
  if (unit === 'b') return `${Math.round(bytes).toLocaleString()} B`;
  const divisor = SIZE_UNIT_MULTIPLIER[unit];
  const v = bytes / divisor;
  const digits = v < 10 ? 2 : v < 100 ? 1 : 0;
  return `${v.toFixed(digits)} ${unit.toUpperCase()}`;
}

// Converts API entries into TreeNodes. Sizes arrive separately via
// /filesystem/size (see measureSizes), so each node starts with
// sizingPending=true and gets sized in the background. Initial sort is
// directories first then alphabetic — a reasonable layout to look at
// while sizes stream in. A single re-sort by size desc happens once
// measurement finishes.
function entriesToTreeNodes(entries: FilesystemEntry[], basePath: string): TreeNode[] {
  const nodes: TreeNode[] = entries.map((e) => {
    const path = basePath === '/' ? '/' + e.name : basePath.replace(/\/+$/, '') + '/' + e.name;
    return {
      name: e.name,
      path,
      size: '',
      sizeBytes: 0,
      percent: 0,
      children: [],
      loaded: !e.isDirectory,
      loading: false,
      expanded: false,
      isLeaf: !e.isDirectory,
      sizingPending: true,
    };
  });

  return nodes.sort((a, b) => {
    if (a.isLeaf !== b.isLeaf) return a.isLeaf ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
}

// Sorts a measured children array by size desc, pushing failed rows to the
// bottom — used as the final pass after measureSizes finishes so the user
// sees their fully-sized tree in the same big-stuff-first order the old
// du-based UI delivered.
function sortBySizeDesc(nodes: TreeNode[]): void {
  nodes.sort((a, b) => {
    const aNeg = a.sizeBytes < 0, bNeg = b.sizeBytes < 0;
    if (aNeg && !bNeg) return 1;
    if (!aNeg && bNeg) return -1;
    return b.sizeBytes - a.sizeBytes;
  });
}

// ─── Color palette for sunburst rings ────────────────────────────────

// Alternating-hue order. Grouping reds/pinks/purples next to each other
// (the old Material 500-palette ordering) made top-level folders look
// monochromatic when the biggest 3–4 happened to sit in adjacent slots.
// Interleaving warm/cool/green/yellow keeps neighboring ring colors
// visually distinct at a glance.
const RING_COLORS = [
  '#E53935', // red
  '#1E88E5', // blue
  '#43A047', // green
  '#FB8C00', // orange
  '#8E24AA', // purple
  '#00ACC1', // cyan
  '#F4511E', // deep orange
  '#3949AB', // indigo
  '#7CB342', // light green
  '#D81B60', // pink
  '#00897B', // teal
  '#FDD835', // yellow
  '#5E35B1', // deep purple
  '#039BE5', // light blue
  '#C0CA33', // lime
  '#FFB300', // amber
  '#78909C', // blue grey
  '#6D4C41', // brown
  '#FF6720', // brand orange
  '#26A69A', // teal accent
];

function getColor(index: number): string {
  return RING_COLORS[index % RING_COLORS.length];
}

// ─── Sunburst Chart (SVG) ────────────────────────────────────────────

interface SunburstProps {
  root: TreeNode | null;
  hoveredPath: string | null;
  onHover: (path: string | null) => void;
  onClick: (node: TreeNode) => void;
  sizeUnit: SizeUnit;
  loading?: boolean;
}

function SunburstChart({ root, hoveredPath, onHover, onClick, sizeUnit, loading }: SunburstProps) {
  const size = 360;
  const cx = size / 2;
  const cy = size / 2;
  const maxDepth = 3;
  const innerRadius = 40;
  const ringWidth = (cx - innerRadius - 10) / maxDepth;

  if (!root || root.children.length === 0) {
    return (
      <Box sx={{ width: size, height: size, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {loading ? (
          <>
            <CircularProgress size={24} sx={{ color: colors.primary.main }} />
            <Typography color="text.secondary" variant="body2">Scanning…</Typography>
          </>
        ) : (
          <Typography color="text.secondary" variant="body2">Scan a directory to see chart</Typography>
        )}
      </Box>
    );
  }

  const arcs: { d: string; fill: string; opacity: number; node: TreeNode; depth: number }[] = [];
  // Baobab-style labels: inline tangent text on arcs wide enough to hold
  // the folder name. Populated during buildArcs so we can place labels
  // exactly at each slice's midpoint and read the truncated name off
  // the arc's angular span.
  const labels: { x: number; y: number; text: string; rotate: number; fontSize: number }[] = [];

  function buildArcs(nodes: TreeNode[], startAngle: number, endAngle: number, depth: number, colorIdx: number) {
    if (depth > maxDepth || nodes.length === 0) return;

    // Skip denied rows (sizeBytes = -1); they'd otherwise shrink the
    // total and distort everyone else's sweep angle.
    const total = nodes.reduce((s, n) => (n.sizeBytes < 0 ? s : s + n.sizeBytes), 0);
    if (total === 0) return;

    let currentAngle = startAngle;

    nodes.forEach((node, i) => {
      const fraction = node.sizeBytes / total;
      const sweep = (endAngle - startAngle) * fraction;

      if (sweep < 0.02) { // skip tiny slices
        currentAngle += sweep;
        return;
      }

      const r1 = innerRadius + (depth - 1) * ringWidth;
      const r2 = innerRadius + depth * ringWidth;
      const a1 = currentAngle;
      const a2 = currentAngle + sweep;
      const ci = depth === 1 ? i : colorIdx;

      // Arc path
      const x1o = cx + r2 * Math.cos(a1);
      const y1o = cy + r2 * Math.sin(a1);
      const x2o = cx + r2 * Math.cos(a2);
      const y2o = cy + r2 * Math.sin(a2);
      const x1i = cx + r1 * Math.cos(a2);
      const y1i = cy + r1 * Math.sin(a2);
      const x2i = cx + r1 * Math.cos(a1);
      const y2i = cy + r1 * Math.sin(a1);
      const large = sweep > Math.PI ? 1 : 0;

      const d = [
        `M ${x1o} ${y1o}`,
        `A ${r2} ${r2} 0 ${large} 1 ${x2o} ${y2o}`,
        `L ${x1i} ${y1i}`,
        `A ${r1} ${r1} 0 ${large} 0 ${x2i} ${y2i}`,
        'Z',
      ].join(' ');

      const isHovered = hoveredPath === node.path;
      const baseColor = getColor(ci);
      // Baobab-style: full opacity for every depth. The previous
      // 1 → 0.75 → 0.55 fade made outer rings look washed out. Slight
      // dip on non-hover keeps the hovered arc popping.
      const opacity = isHovered ? 1 : 0.92;

      arcs.push({ d, fill: baseColor, opacity, node, depth });

      // Inline label: only when the arc is wide enough to fit text
      // readably. Rough char budget from arc length at mid radius.
      const midAngle = a1 + sweep / 2;
      const midRadius = (r1 + r2) / 2;
      const arcLen = midRadius * sweep; // px along arc
      const charPx = 5.5; // approx width of one ~10px char
      const maxChars = Math.floor(arcLen / charPx) - 1;
      if (maxChars >= 3) {
        const rawName = node.name.replace(/^\//, '');
        const name = rawName.length > maxChars ? rawName.slice(0, maxChars - 1) + '…' : rawName;
        // Rotate text tangent to the arc. Flip on the bottom half so
        // it never reads upside-down.
        let rotateDeg = (midAngle * 180) / Math.PI + 90;
        if (rotateDeg > 90 && rotateDeg < 270) rotateDeg -= 180;
        labels.push({
          x: cx + midRadius * Math.cos(midAngle),
          y: cy + midRadius * Math.sin(midAngle),
          text: name,
          rotate: rotateDeg,
          fontSize: depth === 1 ? 11 : 9,
        });
      }

      if (node.children.length > 0 && node.expanded) {
        buildArcs(node.children, a1, a2, depth + 1, ci);
      }

      currentAngle += sweep;
    });
  }

  buildArcs(root.children, -Math.PI / 2, 1.5 * Math.PI, 1, 0);

  // Center + arc-tooltip text always use the auto format regardless of
  // the user's chosen tree-column unit. The center is a one-glance total,
  // not a precision readout — bytes there are hard to read even with
  // font scaling. Users who want the exact number can still see it in
  // the tree column or via the per-row Size tooltip.
  const centerText = formatSize(root.sizeBytes, 'auto');
  const centerFontSize = centerText.length <= 8 ? 13
    : centerText.length <= 11 ? 11
    : 9;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Center circle */}
      <circle cx={cx} cy={cy} r={innerRadius} fill="#F5F5F5" stroke="#E0E0E0" strokeWidth={1} />
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize={centerFontSize} fill="#444" fontWeight={700}>
        {centerText}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize={10} fill="#888">
        {(() => {
          const label = !root.name || root.name === '/' ? 'root' : root.name;
          // Trim to fit ~9 chars inside the 40-px-radius inner circle.
          return label.length > 9 ? `…${label.slice(-8)}` : label;
        })()}
      </text>

      {/* Arcs */}
      {arcs.map((arc, i) => (
        <path
          key={i}
          d={arc.d}
          fill={arc.fill}
          opacity={arc.opacity}
          stroke="#fff"
          strokeWidth={1}
          style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
          onMouseEnter={() => onHover(arc.node.path)}
          onMouseLeave={() => onHover(null)}
          onClick={(e) => { e.stopPropagation(); onClick(arc.node); }}
        >
          <title>{`${arc.node.name}\n${formatSize(arc.node.sizeBytes, sizeUnit)}`}</title>
        </path>
      ))}

      {/* Inline arc labels (Baobab-style). White fill + dark stroke halo
          keeps names legible on any ring color. Pointer events off so
          the label doesn't swallow the arc's hover/click. */}
      {labels.map((l, i) => (
        <text
          key={`lbl-${i}`}
          x={l.x}
          y={l.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={l.fontSize}
          fontWeight={600}
          fill="#fff"
          stroke="rgba(0,0,0,0.55)"
          strokeWidth={2.5}
          paintOrder="stroke fill"
          transform={`rotate(${l.rotate} ${l.x} ${l.y})`}
          style={{ pointerEvents: 'none', fontFamily: 'inherit' }}
        >
          {l.text}
        </text>
      ))}
    </svg>
  );
}

// ─── Usage bar (inline, like Baobab) ─────────────────────────────────

// Semantic fill color for the Usage bar based on % of parent. Previously
// bars shared the RING_COLORS palette with the sunburst arcs, which made
// everything look alarmingly red regardless of actual size. Non-technical
// viewers just saw "red line of varying length" with no quick-read
// signal. Traffic-light thresholds let the bar convey urgency at a
// glance: green = this folder is a minor slice, amber = notable, red =
// dominant. Matches the filesystem-usage bar at the top of the card.
function usageColorForPercent(percent: number): string {
  if (percent >= 60) return colors.error.main; // red — dominant
  if (percent >= 25) return colors.warning.main; // amber — notable
  return '#43A047';                     // green — minor
}

function UsageBar({ percent }: { percent: number }) {
  const color = usageColorForPercent(percent);
  return (
    <Box sx={{ width: 80, height: 14, bgcolor: '#F0F0F0', borderRadius: 0.5, overflow: 'hidden', flexShrink: 0 }}>
      <Box sx={{ width: `${Math.min(percent, 100)}%`, height: '100%', bgcolor: color, borderRadius: 0.5, transition: 'width 0.3s' }} />
    </Box>
  );
}

// ─── Tree row ────────────────────────────────────────────────────────

interface TreeRowProps {
  node: TreeNode;
  depth: number;
  colorIndex: number;
  hoveredPath: string | null;
  onHover: (path: string | null) => void;
  onToggle: (node: TreeNode) => void;
  onRetry: (node: TreeNode) => void;
  sizeUnit: SizeUnit;
  // Name of the row's parent folder. Used in the % column tooltip so
  // users understand "88.7%" means 88.7% of *that* parent, not of the
  // whole disk. Empty string at root level.
  parentName: string;
  // Column visibility from the view-options menu. Both default on;
  // hiding either lets the user declutter without losing information
  // since size + % + bar are all views of the same quantity.
  showUsageCol: boolean;
  showPercentCol: boolean;
  // True while a size scan is streaming. Opening an un-expanded folder is
  // blocked during a scan (it would cancel the in-flight run), so rows in
  // that state render a not-allowed cursor instead of a misleading pointer.
  scanning: boolean;
}

function TreeRow({ node, depth, colorIndex, hoveredPath, onHover, onToggle, onRetry, sizeUnit, parentName, showUsageCol, showPercentCol, scanning }: TreeRowProps) {
  const isHovered = hoveredPath === node.path;
  const denied = node.permissionDenied === true;
  const measuring = node.sizingPending === true;
  const hasChildren = !node.isLeaf && !denied;
  // Opening this row would interrupt the active scan — gate the click and
  // the cursor. Collapsing an already-open row stays allowed.
  const openBlocked = scanning && hasChildren && !node.expanded;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.4,
          px: 0.5,
          borderRadius: 0.5,
          bgcolor: isHovered ? 'action.hover' : 'transparent',
          cursor: openBlocked ? 'not-allowed' : hasChildren ? 'pointer' : 'default',
          opacity: denied ? 0.6 : 1,
          '&:hover': { bgcolor: denied ? 'action.hover' : 'action.hover' },
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 32,
          // Reorder fade — TreeRow is keyed with completionTick at the top
          // level, so this animation fires once each time measurement
          // finishes and the sort runs. Ease-out-quart per project motion
          // guidance: settle, don't bounce.
          '@keyframes rowSettle': {
            from: { opacity: 0.35 },
            to: { opacity: 1 },
          },
          animation: 'rowSettle 280ms cubic-bezier(0.22, 1, 0.36, 1)',
          // Group selector lets the in-row hover-only retry button know
          // when the row is being hovered.
          '&:hover .row-retry-btn': { opacity: 1 },
        }}
        onMouseEnter={() => onHover(node.path)}
        onMouseLeave={() => onHover(null)}
        onClick={() => { if (!openBlocked && hasChildren) onToggle(node); }}
      >
        {/* Size — moved to the far left (ncdu-style). Numbers stay
            right-aligned within the fixed column so magnitudes line up.
            Widen when the user picked raw Bytes so the locale-grouped
            number (e.g. "1,234,567,890 B") doesn't overflow or wrap.
            While measuring, render a tiny spinner so the row doesn't look
            like a failed/denied one. When a non-auto unit is active,
            hovering shows the human-readable equivalent. */}
        <Tooltip
          title={!denied && !measuring && sizeUnit !== 'auto' ? `≈ ${formatSize(node.sizeBytes, 'auto')}` : ''}
          placement="right"
          disableHoverListener={denied || measuring || sizeUnit === 'auto'}
        >
          <Box sx={{
            width: sizeUnit === 'b' ? 140 : 80,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            {measuring ? (
              <CircularProgress size={10} thickness={6} sx={{ color: '#BBB' }} />
            ) : (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: 600,
                  color: denied ? '#999' : 'inherit',
                }}
              >
                {denied ? '—' : formatSize(node.sizeBytes, sizeUnit)}
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Tree column — expand/folder icons + name. Indentation lives
            here (not on the row) so the leading Size column stays aligned
            across depths. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0, pl: depth * 2.5 }}>
          {/* Expand/collapse icon */}
          <Box sx={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {node.loading ? (
              <CircularProgress size={14} />
            ) : hasChildren ? (
              node.expanded ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#888' }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: '#888' }} />
            ) : null}
          </Box>

          {/* Folder / file / lock-with-retry icon. The lock state now offers
              a one-click retry: hovering swaps the lock for a Replay button
              that remeasures just this row. Communicates "this isn't a
              dead-end, just a hiccup." */}
          {denied ? (
            <Box sx={{ position: 'relative', width: 16, height: 16, flexShrink: 0 }}>
              <LockIcon
                className="row-lock-icon"
                sx={{ fontSize: 16, color: '#999', position: 'absolute', inset: 0 }}
              />
              <Tooltip title="Retry" placement="top">
                <IconButton
                  className="row-retry-btn"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onRetry(node); }}
                  sx={{
                    position: 'absolute',
                    inset: -4,
                    opacity: 0,
                    transition: 'opacity 120ms ease-out',
                    bgcolor: 'background.paper',
                    color: colors.primary.main,
                    p: 0.25,
                    '&:hover': { bgcolor: 'background.paper', color: colors.primary.hover },
                  }}
                >
                  <ReplayIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : hasChildren ? (
            node.expanded
              ? <FolderOpenIcon sx={{ fontSize: 16, color: colors.warning.main, flexShrink: 0 }} />
              : <FolderIcon sx={{ fontSize: 16, color: colors.warning.main, flexShrink: 0 }} />
          ) : (
            <FileIcon sx={{ fontSize: 16, color: '#AAA', flexShrink: 0 }} />
          )}

          {/* Name */}
          <Typography
            variant="body2"
            sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12, fontWeight: depth === 0 ? 600 : 400, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {node.name}
          </Typography>
        </Box>

        {/* Usage bar — blanked out for denied or still-measuring rows
            since percent isn't meaningful until siblings finish sizing.
            Hidden entirely when the user turned the Usage column off. */}
        {showUsageCol && (denied || measuring ? (
          <Box sx={{ width: 80, height: 14, flexShrink: 0 }} />
        ) : (
          <UsageBar percent={node.percent} />
        ))}

        {/* Percent — tooltip makes the "of what?" explicit so non-technical
            viewers don't wonder whether this is % of parent or % of disk. */}
        {showPercentCol && (
          <Tooltip
            title={denied || measuring ? '' : parentName ? `${node.percent.toFixed(1)}% of "${parentName}"` : `${node.percent.toFixed(1)}% of the scanned root`}
            placement="left"
            disableHoverListener={denied || measuring}
          >
            <Typography variant="caption" sx={{ width: 42, textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: '#666', flexShrink: 0 }}>
              {denied || measuring ? '—' : node.percent > 0 ? `${node.percent.toFixed(1)}%` : '0.0%'}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Children */}
      <Collapse in={node.expanded} timeout={200} unmountOnExit>
        {node.children.map((child, i) => (
          <TreeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            colorIndex={depth === 0 ? i : colorIndex}
            hoveredPath={hoveredPath}
            onHover={onHover}
            onToggle={onToggle}
            onRetry={onRetry}
            sizeUnit={sizeUnit}
            parentName={node.name}
            showUsageCol={showUsageCol}
            showPercentCol={showPercentCol}
            scanning={scanning}
          />
        ))}
      </Collapse>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export function StorageExplorer({ deviceId, isOnline, showAdvancedControls = false }: StorageExplorerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rootNode, setRootNode] = useState<TreeNode | null>(null);
  // Streaming-size progress shown inline inside the card body (in the spot
  // that used to hold the indeterminate LinearProgress). null = not
  // measuring; folder is the path being walked so the user can tell which
  // subtree the spinner belongs to when they're drilled in.
  const [measureProgress, setMeasureProgress] = useState<{ done: number; total: number; folder: string } | null>(null);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);
  // Path that was last scanned. Drives Rescan's anchor and the default
  // for the first Scan. Anchored at the filesystem root `/` so the operator
  // sees the whole device in one pass.
  const [currentRoot, setCurrentRoot] = useState<string>('/');
  // Typed "device firmware too old" state. The deviceService rethrows
  // HTTP 501 responses as FirmwareOutdatedError; when caught we set this
  // and render a dedicated affordance instead of a generic red Alert.
  const [firmwareOutdated, setFirmwareOutdated] = useState<{ minVersion: string } | null>(null);
  // Tick that bumps when measureSizes finishes — used to drive a brief
  // CSS fade animation on rows so the post-sort reorder feels deliberate
  // instead of jumpy. Read by TreeRow via a key suffix.
  const [completionTick, setCompletionTick] = useState(0);

  // Tooltip for sunburst hover
  const [tooltipNode, setTooltipNode] = useState<TreeNode | null>(null);

  // Size unit override, persisted so QC can set their preferred unit once.
  // Default 'kb' — raw bytes are too noisy to eyeball. Summary areas
  // (subheader, sunburst center, hover info) always fall back to auto for
  // readability, so the KB default only affects the Size column.
  const [sizeUnit, setSizeUnit] = useState<SizeUnit>(() => {
    if (typeof window === 'undefined') return 'kb';
    const stored = localStorage.getItem('disk-usage-unit');
    if (stored === 'auto' || stored === 'b' || stored === 'kb' || stored === 'mb' || stored === 'gb' || stored === 'tb') return stored;
    return 'kb';
  });
  const [unitAnchor, setUnitAnchor] = useState<HTMLElement | null>(null);
  const handleUnitChange = useCallback((next: SizeUnit) => {
    setSizeUnit(next);
    try { localStorage.setItem('disk-usage-unit', next); } catch { /* storage quota / sandbox */ }
    setUnitAnchor(null);
  }, []);

  // Column visibility. Both columns are conceptually redundant — Usage
  // shows the same % visually, % shows it as text, and the Size column
  // already carries the magnitude. Default both OFF (hidden) to keep the
  // table lean; power users can switch either back on via the view-options
  // menu (persisted per-column).
  const [showUsageCol, setShowUsageCol] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('disk-usage-col-usage') === 'on';
  });
  const [showPercentCol, setShowPercentCol] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('disk-usage-col-percent') === 'on';
  });
  const toggleUsageCol = useCallback(() => {
    setShowUsageCol((prev) => {
      const next = !prev;
      try { localStorage.setItem('disk-usage-col-usage', next ? 'on' : 'off'); } catch { /* sandbox */ }
      return next;
    });
  }, []);
  const togglePercentCol = useCallback(() => {
    setShowPercentCol((prev) => {
      const next = !prev;
      try { localStorage.setItem('disk-usage-col-percent', next ? 'on' : 'off'); } catch { /* sandbox */ }
      return next;
    });
  }, []);

  // Show/hide the sunburst. Always starts hidden — the previous
  // localStorage persistence was dropped so a viewer who toggled it
  // on weeks ago doesn't get hit with the dense chart unexpectedly
  // on next visit. Power users can still flip it on per-session via
  // the donut icon when `showAdvancedControls` is enabled.
  const [showChart, setShowChart] = useState<boolean>(false);
  const toggleChart = useCallback(() => setShowChart((prev) => !prev), []);

  // Drives the partial-failure hint and degraded banner. Only counts rows
  // at the currently-rendered root level (children of rootNode) — that's
  // the granularity the post-completion summary speaks to. Deeper failures
  // get their own per-folder hints when the user drills in.
  const rootStats = useMemo(() => {
    if (!rootNode) return { failed: 0, measured: 0, pending: 0, total: 0 };
    let failed = 0, measured = 0, pending = 0;
    rootNode.children.forEach(c => {
      if (c.sizingPending) pending++;
      else if (c.sizeBytes < 0) failed++;
      else measured++;
    });
    return { failed, measured, pending, total: rootNode.children.length };
  }, [rootNode, completionTick]); // completionTick forces recompute on re-sort

  // Render guidance for the post-scan failure hint. null while still
  // measuring (>0 pending), 'partial' when 1+ failed but <50%, 'degraded'
  // when ≥50% of rows came back unmeasurable.
  const failureMode = useMemo<'partial' | 'degraded' | null>(() => {
    if (rootStats.pending > 0) return null;
    if (rootStats.failed === 0 || rootStats.total === 0) return null;
    return rootStats.failed / rootStats.total >= 0.5 ? 'degraded' : 'partial';
  }, [rootStats]);

  // Sunburst zoom target. The chart caps at 3 rings for legibility; to
  // explore deeper we re-anchor it to a subtree instead of rendering
  // ring-4+. Stored as a path (not a node ref) so stale references
  // don't survive tree mutations. null = anchored at rootNode.
  const [sunburstPath, setSunburstPath] = useState<string | null>(null);
  const sunburstRoot = useMemo(() => {
    if (!rootNode) return null;
    if (!sunburstPath) return rootNode;
    const walk = (n: TreeNode): TreeNode | null => {
      if (n.path === sunburstPath) return n;
      for (const c of n.children) {
        const hit = walk(c);
        if (hit) return hit;
      }
      return null;
    };
    return walk(rootNode) || rootNode;
  }, [rootNode, sunburstPath]);
  // Breadcrumb trail from rootNode down to the sunburst anchor (exclusive
  // of the anchor, so clicking the last crumb zooms out one level).
  const sunburstCrumbs = useMemo(() => {
    if (!rootNode || !sunburstRoot || sunburstRoot === rootNode) return [] as TreeNode[];
    const trail: TreeNode[] = [];
    const walk = (n: TreeNode, acc: TreeNode[]): boolean => {
      if (n.path === sunburstRoot.path) {
        trail.push(...acc);
        return true;
      }
      for (const c of n.children) {
        if (walk(c, [...acc, n])) return true;
      }
      return false;
    };
    walk(rootNode, []);
    return trail;
  }, [rootNode, sunburstRoot]);

  // Deep-clone helper to trigger re-render on tree mutation. Declared
  // early because measureSizes captures it in its useCallback deps.
  const updateTree = useCallback(() => {
    setRootNode(prev => prev ? { ...prev } : null);
  }, []);

  // One HTTP call returns the directory's entries without sizes. Sizes
  // arrive separately via measureSizes() — a streaming approach so a
  // slow /system or /data subtree never blocks the whole listing.
  const fetchChildren = useCallback(async (path: string): Promise<TreeNode[]> => {
    const result = await deviceService.listFilesystem(deviceId, path);
    if (!result.success) {
      throw new Error('Filesystem listing failed');
    }
    const entries: FilesystemEntry[] = Array.isArray(result.entries) ? result.entries : [];
    return entriesToTreeNodes(entries, path);
  }, [deviceId]);

  // Concurrent worker pool that measures each child's recursive size and
  // mutates the TreeNode in place. After every result we recompute sibling
  // percents and trigger a tree re-render, so rows light up progressively
  // instead of waiting for the slowest entry. A single sort-by-size pass
  // runs at the end so the user lands on the familiar big-stuff-first
  // layout once measurement completes. Pool size = 4 strikes a balance
  // between filling the browser's HTTP/1.1 connection limit and not
  // hammering the device daemon. Stale runs are cancelled by a token —
  // a rescan or re-expand bumps the token, in-flight workers exit early
  // so their stale results don't overwrite fresh state.
  const measureRunRef = useRef(0);
  // True while a measureSizes run is streaming sizes. Opening a folder
  // mid-run lazy-loads it and kicks off a nested measureSizes, which bumps
  // measureRunRef and cancels the in-flight scan — handleToggle gates on
  // this ref to keep the user from interrupting their own scan. A ref (not
  // state) so handleToggle stays referentially stable across re-renders.
  const measuringRef = useRef(false);
  const measureSizes = useCallback(async (parent: TreeNode) => {
    const myRun = ++measureRunRef.current;
    const children = parent.children;
    if (children.length === 0) return;

    const total = children.length;
    let done = 0;
    measuringRef.current = true;
    setMeasureProgress({ done: 0, total, folder: parent.path });

    const recomputePercents = () => {
      const measurableTotal = children.reduce(
        (s, c) => (c.sizingPending || c.sizeBytes < 0 ? s : s + c.sizeBytes),
        0,
      );
      children.forEach(c => {
        c.percent = c.sizingPending || c.sizeBytes < 0 || measurableTotal <= 0
          ? 0
          : (c.sizeBytes / measurableTotal) * 100;
      });
    };

    let cursor = 0;
    const worker = async () => {
      while (true) {
        if (measureRunRef.current !== myRun) return; // cancelled
        const idx = cursor++;
        if (idx >= children.length) return;
        const node = children[idx];
        try {
          const resp = await deviceService.getItemSize(deviceId, node.path, !node.isLeaf);
          if (measureRunRef.current !== myRun) return;
          if (resp.sizeBytes < 0) {
            node.sizeBytes = -1;
            node.permissionDenied = true;
          } else {
            node.sizeBytes = resp.sizeBytes;
            node.permissionDenied = false;
          }
        } catch (err) {
          if (measureRunRef.current !== myRun) return;
          // One firmware-outdated error means the whole flow is dead in
          // the water — drop into the typed state and stop sizing.
          if (err instanceof FirmwareOutdatedError) {
            setFirmwareOutdated({ minVersion: err.minVersion });
            measureRunRef.current = -1; // cancel all workers
            return;
          }
          node.sizeBytes = -1;
          node.permissionDenied = true;
        } finally {
          node.sizingPending = false;
        }
        done++;
        setMeasureProgress({ done, total, folder: parent.path });
        recomputePercents();
        updateTree();
      }
    };

    const pool = Math.min(4, children.length);
    try {
      await Promise.all(Array.from({ length: pool }, worker));
      if (measureRunRef.current !== myRun) return;
      sortBySizeDesc(children);
      recomputePercents();
      // Disabled: never re-total a folder from its children — every size comes
      // only from /filesystem/size. NOTE: the scanned root has no
      // /filesystem/size of its own, so with this off its total (and the
      // sunburst center) will read 0 until seeded another way.
      // if (parent.sizeBytes <= 0) {
      //   parent.sizeBytes = children.reduce((s, c) => (c.sizeBytes > 0 ? s + c.sizeBytes : s), 0);
      // }
      updateTree();
      // Bump completion tick so rows get a brief fade-in animation —
      // softens the sort reorder so it doesn't read as a jump.
      setCompletionTick(t => t + 1);
    } finally {
      if (measureRunRef.current === myRun) {
        setMeasureProgress(null);
        measuringRef.current = false;
      }
    }
  }, [deviceId, updateTree]);

  // Scan a directory. Default anchor is the filesystem root `/` so the
  // operator sees the whole device, but the path picker lets them point
  // anywhere. Setting currentRoot here is what makes Rescan return to the
  // same anchor instead of jumping back to the default.
  const scanRoot = useCallback(async (scanPath: string = currentRoot) => {
    setLoading(true);
    setError(null);
    setFirmwareOutdated(null);
    try {
      const basePath = scanPath;
      const children = await fetchChildren(basePath);

      const root: TreeNode = {
        name: basePath,
        path: basePath,
        size: '',
        // Seeded to 0 here; measureSizes fills it from the children sum
        // once recursive sizing completes.
        sizeBytes: 0,
        percent: 100,
        children,
        loaded: true,
        loading: false,
        expanded: true,
        isLeaf: false,
      };

      setRootNode(root);
      setScanned(true);
      setCurrentRoot(basePath);
      // Drop any previous sunburst zoom so a rescan starts fresh at the
      // new tree's root.
      setSunburstPath(null);
      // Kick off per-row size measurement in the background. The listing
      // is already visible; sizes light up progressively as each request
      // resolves. Fire-and-forget — measureSizes mutates the tree nodes
      // and re-renders itself; awaiting would defeat the streaming UX.
      void measureSizes(root);
    } catch (err) {
      if (err instanceof FirmwareOutdatedError) {
        setFirmwareOutdated({ minVersion: err.minVersion });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to scan filesystem');
      }
    } finally {
      setLoading(false);
    }
  }, [currentRoot, deviceId, fetchChildren, measureSizes]);

  // Toggle expand/collapse a node — lazy-loads children on first expand.
  // Listing returns instantly; sizes populate in the background via
  // measureSizes so the user sees the directory structure without waiting
  // on /system or /data recursive walks.
  const handleToggle = useCallback(async (node: TreeNode) => {
    if (node.expanded) {
      node.expanded = false;
      updateTree();
      return;
    }

    // A scan is streaming sizes. Opening another folder would lazy-load it
    // and start a nested measureSizes run, cancelling the active scan — so
    // block opening until it settles. Collapsing (handled above) stays free.
    if (measuringRef.current) return;

    if (!node.loaded) {
      node.loading = true;
      updateTree();
      try {
        const children = await fetchChildren(node.path);
        node.children = children;
        node.loaded = true;
        node.isLeaf = children.length === 0;
      } catch (err) {
        if (err instanceof FirmwareOutdatedError) {
          setFirmwareOutdated({ minVersion: err.minVersion });
        }
        node.isLeaf = true;
      } finally {
        node.loading = false;
      }
    }

    node.expanded = true;
    updateTree();
    if (node.children.length > 0 && node.children.some(c => c.sizingPending)) {
      void measureSizes(node);
    }
  }, [fetchChildren, updateTree, measureSizes]);

  // Helper: find the parent TreeNode of a given child path in the current
  // tree. Needed so per-row retry can find sibling context for percent
  // recomputation without re-fetching the directory. O(n) walk is fine —
  // a single tree fits in memory many times over.
  const findParent = useCallback((target: TreeNode): TreeNode | null => {
    if (!rootNode) return null;
    const walk = (n: TreeNode): TreeNode | null => {
      for (const c of n.children) {
        if (c.path === target.path) return n;
        const hit = walk(c);
        if (hit) return hit;
      }
      return null;
    };
    return walk(rootNode);
  }, [rootNode]);

  // Retry size measurement for a single failed row. The row goes back
  // to the sizing-pending state, the call goes out, sibling percents get
  // recomputed when it lands. Stops short of triggering a full
  // measureSizes run on the parent so we don't re-measure rows that
  // already succeeded.
  const retryRow = useCallback(async (node: TreeNode) => {
    node.sizingPending = true;
    node.permissionDenied = false;
    node.sizeBytes = 0;
    updateTree();
    try {
      const resp = await deviceService.getItemSize(deviceId, node.path, !node.isLeaf);
      if (resp.sizeBytes < 0) {
        node.sizeBytes = -1;
        node.permissionDenied = true;
      } else {
        node.sizeBytes = resp.sizeBytes;
      }
    } catch (err) {
      if (err instanceof FirmwareOutdatedError) {
        setFirmwareOutdated({ minVersion: err.minVersion });
        return;
      }
      node.sizeBytes = -1;
      node.permissionDenied = true;
    } finally {
      node.sizingPending = false;
    }
    // Recompute sibling percents based on the new size.
    const parent = findParent(node);
    if (parent) {
      const measurable = parent.children.reduce(
        (s, c) => (c.sizingPending || c.sizeBytes < 0 ? s : s + c.sizeBytes),
        0,
      );
      parent.children.forEach(c => {
        c.percent = c.sizingPending || c.sizeBytes < 0 || measurable <= 0
          ? 0
          : (c.sizeBytes / measurable) * 100;
      });
    }
    updateTree();
  }, [deviceId, updateTree, findParent]);

  // Remeasure every -1 row in the currently visible tree. Called from
  // the partial-failure hint at the bottom of the card after a streaming
  // run completes. We walk the tree once to collect the failed paths,
  // reset them, then fire retryRow for each — fan-out is naturally
  // capped by the browser's per-host HTTP/1.1 connection limit (6).
  const retryAllFailed = useCallback(async () => {
    if (!rootNode) return;
    const failed: TreeNode[] = [];
    const walk = (n: TreeNode) => {
      n.children.forEach(c => {
        if (c.permissionDenied && c.sizeBytes < 0) failed.push(c);
        walk(c);
      });
    };
    walk(rootNode);
    if (failed.length === 0) return;
    await Promise.all(failed.map(n => retryRow(n)));
  }, [rootNode, retryRow]);


  const handleSunburstClick = useCallback((node: TreeNode) => {
    // Still expand the matching tree row so the two views stay in sync.
    handleToggle(node);
    // Zoom the sunburst into this node if it has children — otherwise
    // there's nothing new to draw (leaves / denied rows stay on the
    // current view).
    if (!node.isLeaf && !node.permissionDenied) {
      setSunburstPath(node.path);
    }
  }, [handleToggle]);

  const zoomSunburstTo = useCallback((path: string | null) => {
    setSunburstPath(path);
  }, []);

  const handleHover = useCallback((path: string | null) => {
    setHoveredPath(path);
    if (path && rootNode) {
      // Find the node for tooltip
      const findNode = (n: TreeNode): TreeNode | null => {
        if (n.path === path) return n;
        for (const c of n.children) {
          const found = findNode(c);
          if (found) return found;
        }
        return null;
      };
      setTooltipNode(findNode(rootNode));
    } else {
      setTooltipNode(null);
    }
  }, [rootNode]);

  // Display path for the progress popup. Long paths get middle-truncated
  // so the Snackbar doesn't grow wider than the screen.
  const progressFolder = measureProgress?.folder ?? '';
  const progressFolderDisplay = progressFolder.length > 40
    ? `${progressFolder.slice(0, 18)}…${progressFolder.slice(-18)}`
    : progressFolder;
  const progressPct = measureProgress
    ? Math.round((measureProgress.done / Math.max(1, measureProgress.total)) * 100)
    : 0;

  return (
    <>
    <Card variant="outlined">
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon sx={{ color: '#666', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>Disk Usage Analyzer</Typography>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {showAdvancedControls && (
              <Tooltip title={showChart ? 'Hide chart' : 'Show chart'}>
                <IconButton size="small" onClick={toggleChart} sx={{ color: showChart ? colors.primary.main : 'text.secondary' }}>
                  {showChart ? <DonutLargeIcon sx={{ fontSize: 18 }} /> : <DonutSmallIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            )}
            {showAdvancedControls && (
              <Tooltip title="Size unit">
                <IconButton
                  size="small"
                  onClick={(e) => setUnitAnchor(e.currentTarget)}
                  sx={{ color: sizeUnit === 'auto' ? 'text.secondary' : colors.primary.main }}
                >
                  <TuneIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Menu
              anchorEl={unitAnchor}
              open={!!unitAnchor}
              onClose={() => setUnitAnchor(null)}
              slotProps={{ paper: { sx: { minWidth: 180 } } }}
            >
              <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 0.5, pb: 0.25, color: 'text.disabled', fontWeight: 700, letterSpacing: 0.5 }}>
                SIZE UNIT
              </Typography>
              {([
                ['auto', 'Auto (smart)'],
                ['b', 'Bytes'],
                ['kb', 'KB'],
                ['mb', 'MB'],
                ['gb', 'GB'],
                ['tb', 'TB'],
              ] as const).map(([value, label]) => (
                <MenuItem key={value} dense selected={sizeUnit === value} onClick={() => handleUnitChange(value)}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {sizeUnit === value ? <CheckIcon sx={{ fontSize: 16, color: colors.primary.main }} /> : null}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{label}</ListItemText>
                </MenuItem>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 0.5, pb: 0.25, color: 'text.disabled', fontWeight: 700, letterSpacing: 0.5 }}>
                COLUMNS
              </Typography>
              <MenuItem dense onClick={toggleUsageCol}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {showUsageCol ? <CheckIcon sx={{ fontSize: 16, color: colors.primary.main }} /> : null}
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Usage bar</ListItemText>
              </MenuItem>
              <MenuItem dense onClick={togglePercentCol}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {showPercentCol ? <CheckIcon sx={{ fontSize: 16, color: colors.primary.main }} /> : null}
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Percent</ListItemText>
              </MenuItem>
            </Menu>
            {/* Scan / Rescan. Anchors at the filesystem root `/` for a
                whole-device view. Rescan returns to the same anchor via
                currentRoot — no picker, no chips, no per-call path argument. */}
            {isOnline && !firmwareOutdated && (
              <Button
                size="small"
                variant="contained"
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                onClick={() => scanRoot()}
                disabled={loading}
                sx={{ bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.primary.hover }, textTransform: 'none' }}
              >
                {scanned ? 'Rescan' : 'Scan'}
              </Button>
            )}
          </Box>
        }
        subheader={
          !isOnline ? 'Device must be online' : 'Scan device to analyze disk usage — data will be ordered by size after measuring is done.'
        }
        subheaderTypographyProps={{ variant: 'caption' }}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {/* Firmware-too-old — typed state surfaced when the backend
            responds 501 with code firmware_outdated. Has its own
            empty-body shape; the path picker / scan controls also hide
            (handled in header) so the operator sees one clear path. */}
        {firmwareOutdated && (
          <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
            <SystemUpdateIcon sx={{ fontSize: 44, color: colors.warning.main, mb: 1.5 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              Disk analysis needs MDM v{firmwareOutdated.minVersion}+
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
              The file explorer service ships with v{firmwareOutdated.minVersion}.
              Update this device's firmware to enable disk analysis.
            </Typography>
          </Box>
        )}

        {/* Offline — dedicated state instead of a greyed-out button.
            The previous Tooltip was hidden behind hover and the disabled
            button looked like a loading state. */}
        {!firmwareOutdated && !isOnline && !scanned && (
          <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
            <WifiOffIcon sx={{ fontSize: 40, color: '#BBB', mb: 1.5 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              Device offline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We can't read the filesystem until the device reconnects.
            </Typography>
          </Box>
        )}

        {/* Typed list error (the listDirectory call itself failed —
            not per-row size failures, which surface inline). */}
        {!firmwareOutdated && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Initial-listing loading bar. Once the listing arrives this
            yields to the streaming-progress strip below. */}
        {!firmwareOutdated && loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main } }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Reading directory…
            </Typography>
          </Box>
        )}

        {/* Streaming-size progress strip. Lives inside the card body now
            (no Snackbar) so the popup doesn't float over unrelated UI on
            the device-detail page. Determinate bar + count + the folder
            being walked, fades out the moment measurement completes. */}
        {!firmwareOutdated && measureProgress && (
          <Box sx={{ mb: 2, animation: 'fadeIn 200ms ease-out' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                Measuring sizes in {progressFolderDisplay}
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: colors.primary.main, flexShrink: 0 }}>
                {measureProgress.done} / {measureProgress.total}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                height: 4,
                borderRadius: 1,
                bgcolor: '#F0F0F0',
                '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main, borderRadius: 1 },
              }}
            />
            {/* Why folders won't open mid-scan: lazy-loading another folder
                starts a nested measurement run that cancels this one, so we
                pause navigation until it settles. Surface that as a note so
                the not-allowed cursor reads as intentional. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
              <LockIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">
                Folder navigation is paused while scanning — opening a folder
                would restart the scan.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Degraded — most sizes failed. Stronger callout than the
            partial-failure hint; suggests the file explorer service may
            be down on the device. */}
        {!firmwareOutdated && failureMode === 'degraded' && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="small" />}
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={retryAllFailed}
                sx={{ textTransform: 'none' }}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={600}>
              Most sizes couldn't be measured
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {rootStats.failed} of {rootStats.total} rows failed.
              The file explorer service may be unresponsive on this device.
            </Typography>
          </Alert>
        )}

        {/* Partial — a few sizes failed but the result is usable. Passive
            tone (text-color only, no banner background) with an inline
            Retry-those affordance. */}
        {!firmwareOutdated && failureMode === 'partial' && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {rootStats.failed} of {rootStats.total} sizes couldn't be measured.
            </Typography>
            <Link
              component="button"
              type="button"
              onClick={retryAllFailed}
              variant="caption"
              sx={{ color: colors.primary.main, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            >
              Retry those
            </Link>
          </Box>
        )}

        {/* Online + never-scanned empty state. Single hero affordance:
            click Scan in the header. The notes below set expectations
            about the streaming sort and warn against running this in
            parallel with other heavy monitors that hammer the same
            device CPU. */}
        {!firmwareOutdated && isOnline && !scanned && !loading && (
          <Box sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <StorageIcon sx={{ fontSize: 40, color: '#DDD', mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Find what's taking up disk space on this device.
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontStyle: 'italic', maxWidth: 460, mx: 'auto', display: 'block' }}
            >
              For best performance, disable live logging and memory monitor before scanning.
            </Typography>
          </Box>
        )}

        {/* Main layout: Tree table + Sunburst chart */}
        {!firmwareOutdated && scanned && rootNode && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            {/* Tree table (left side) */}
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
              {/* Column headers */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.75,
                px: 0.5,
                borderBottom: '2px solid',
                borderColor: 'divider',
                bgcolor: '#FAFAFA',
                borderRadius: '4px 4px 0 0',
              }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ width: sizeUnit === 'b' ? 140 : 80, textAlign: 'right', color: '#444', flexShrink: 0 }}
                >
                  Size
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ flex: 1, pl: 3.5, color: '#444' }}>
                  Folder
                </Typography>
                {showUsageCol && (
                  <Typography variant="caption" fontWeight={700} sx={{ width: 80, textAlign: 'center', color: '#444' }}>
                    Usage
                  </Typography>
                )}
                {showPercentCol && (
                  <Typography variant="caption" fontWeight={700} sx={{ width: 42, textAlign: 'right', color: '#444' }}>
                    %
                  </Typography>
                )}
              </Box>

              {/* Tree rows. Top-level key includes completionTick so a
                  finished measureSizes run remounts the root rows once —
                  the row's CSS @keyframes animation fires and the sort
                  reorder reads as a settle, not a jump. Expanded sub-rows
                  also remount but their state lives on the TreeNode
                  object, so nothing visual is lost. */}
              <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
                {rootNode.children.map((child, i) => (
                  <TreeRow
                    key={`${child.path}-${completionTick}`}
                    node={child}
                    depth={0}
                    colorIndex={i}
                    hoveredPath={hoveredPath}
                    onHover={handleHover}
                    onToggle={handleToggle}
                    onRetry={retryRow}
                    sizeUnit={sizeUnit}
                    parentName={rootNode.name || ''}
                    showUsageCol={showUsageCol}
                    showPercentCol={showPercentCol}
                    scanning={measureProgress !== null}
                  />
                ))}
                {rootNode.children.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No directories found
                  </Typography>
                )}
              </Box>

            </Box>

            {/* Sunburst chart (right side). Hidden when the user toggles
                the chart off so the tree table can use the full width. */}
            {showChart && (
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}>
                {/* Zoom breadcrumb — rendered only when we're drilled in.
                    Each crumb is a click target that jumps the chart
                    back to that ancestor. The final crumb is the
                    current root and is non-clickable. */}
                {sunburstCrumbs.length > 0 && rootNode && sunburstRoot && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap', justifyContent: 'center', fontSize: 11, maxWidth: 360 }}>
                    {[...sunburstCrumbs, sunburstRoot].map((n, i, arr) => {
                      const isLast = i === arr.length - 1;
                      return (
                        <Box key={n.path} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Typography
                            variant="caption"
                            onClick={() => {
                              if (isLast) return;
                              zoomSunburstTo(n.path === rootNode.path ? null : n.path);
                            }}
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: 11,
                              color: isLast ? 'text.primary' : colors.primary.main,
                              fontWeight: isLast ? 600 : 400,
                              cursor: isLast ? 'default' : 'pointer',
                              '&:hover': { textDecoration: isLast ? 'none' : 'underline' },
                              maxWidth: 120,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {n === rootNode ? 'root' : n.name}
                          </Typography>
                          {!isLast && <NavigateNextIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                <SunburstChart
                  root={sunburstRoot}
                  hoveredPath={hoveredPath}
                  onHover={handleHover}
                  onClick={handleSunburstClick}
                  sizeUnit={sizeUnit}
                  loading={loading}
                />
                {/* Hover info — falls back to a hint telling the user
                    click zooms in. */}
                <Box sx={{ textAlign: 'center', minHeight: 36 }}>
                  {tooltipNode ? (
                    <>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {tooltipNode.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatSize(tooltipNode.sizeBytes, sizeUnit)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Hover to see details • Click an arc to zoom in
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
    </>
  );
}
